'use client';
import dynamic from 'next/dynamic';
const Button = dynamic(() => import('de-sxm-reactlib-2/components/atoms/Button/Button'));
const Text = dynamic(() => import('de-sxm-reactlib/components/atoms/Display/Text/Text'));
const InvalidFeedback = dynamic(
    () => import('de-sxm-reactlib-2/components/atoms/Inputs/InvalidFeedback/InvalidFeedback'),
);
import DisplayModal from 'de-sxm-reactlib/components/atoms/Display/Modal/Modal';
import styles from './infotainmentmodal.module.scss';
import { useEffect, useRef, useState } from 'react';
import { YMM } from '..';
import { YMMData } from '../Form/interfaces';
import { setYMMCookies, getYMMCookies } from '@/packages/sxm/ymm';
import { Platform } from '@/packages/sxm/ymm/types';
import { useClickOutside } from '@/hooks';
import { FocusTrap } from 'focus-trap-react';

interface Props {
    onClose: () => void;
}

interface ModalStrings {
    titles: Record<string, string>;
    infoTexts: Record<string, string>;
    buttonTexts: Record<string, string>;
}

const modalStrings: ModalStrings = {
    titles: {
        default: 'What type of vehicle do you have?',
        onlyStreaming: 'You have streaming only.',
        noSupport: "Your vehicle doesn't support any of our Infotainment plans.",
    },
    infoTexts: {
        default:
            'We can show you relevant channel information, content and subscription options specific to the factory-equipped radio in your vehicle.',
        onlyStreaming:
            'Unfortunately, SiriusXM Infotainment is only available on vehicle hardware. You may preview all of our Infotainment offers, but you will be unable to purchase a package.',
        noSupport:
            "Unfortunately, SiriusXM Infotainment is not available on your vehicle's hardware. You may preview all of our Infotainment offers, but you will be unable to purchase a package.",
    },
    buttonTexts: {
        default: 'SHOW MY PACKAGES',
        onlyStreaming: 'SHOW ALL PACKAGES',
        noSupport: 'SHOW ALL PACKAGES',
    },
};

export function InfotainmentModal(props: Props) {
    const { onClose } = props;
    const MODAL_PAGE_NAME = 'overlay:ymm';
    const [modalPage, setModalPage] = useState('default');
    const [ymmValid, setYMMValid] = useState<FieldValidityState>('untouched');
    const [ymm, setYMMData] = useState<YMMData>({
        year: '',
        make: '',
        model: '',
        id: '',
    });
    const [focusPause, setFocusPause] = useState(false);

    const targetRef = useRef<HTMLElement | null>(null);
    const ref = useRef<HTMLDivElement>(null);

    const MODAL_CLICK_THROUGH_EXCEPTIONS_SELECTOR = [
        '#onetrust-consent-sdk', // One Trust cookie consent banner
        '.LPMcontainer', // Live Person chat invocation buttons
        '#lpChat', // Interactive Live Person chat window
    ].join(', ');

    useClickOutside(ref, {}, (state, event) => {
        if (event) {
            if (event.target) {
                const target = event.target as HTMLElement;
                const closest = target.closest(MODAL_CLICK_THROUGH_EXCEPTIONS_SELECTOR);
                if (closest) {
                    targetRef.current = target;
                }
                setFocusPause(!!closest);
            }
        }
    });

    useEffect(() => {
        if (focusPause && targetRef.current) {
            targetRef.current.click();
        }
    }, [focusPause]);

    useEffect(() => {
        const ymmCookies = getYMMCookies();
        const carData = ymmCookies.sxm_radio.split('|');

        if (carData.length > 1) {
            setYMMData({
                year: carData[1],
                make: carData[2],
                model: carData[3],
                id: carData[4],
            });
            onClose();
        }
    }, [onClose]);

    function saveCookies(event: React.MouseEvent<HTMLButtonElement>) {
        let platform: Platform = 'siriusxm';

        if (modalPage === 'noSupport') {
            props.onClose();
            document.body.dispatchEvent(new CustomEvent('ymmUpdated'));
            return;
        }

        if (!ymm.id) {
            setYMMValid('invalid');
            return;
        } else {
            setYMMValid('valid');
        }

        if (ymm && ymm.capabilities) {
            if (ymm.capabilities.siriusRadio) {
                platform = 'sirius';
            } else if (ymm.capabilities.xmRadio) {
                platform = 'xm';
            }
        }

        setYMMCookies({
            sxm_radio: `YMM|${ymm.year}|${ymm.make}|${ymm.model}|${ymm.id}`,
            sxm_platform: platform,
            inft_ymm: ymm.capabilities?.infotainment.join('|') || '',
            ymm_bar: '', // TODO
            roadblockbypass: '', // TODO
        });

        if (
            ymm &&
            !ymm.capabilities?.travelLinkFuel &&
            !ymm.capabilities?.localForecast &&
            !ymm.capabilities?.travelLinkMovies &&
            !ymm.capabilities?.navTraffic &&
            !ymm.capabilities?.navWeather &&
            !ymm.capabilities?.travelLinkParking &&
            !ymm.capabilities?.siriusxmTraffic &&
            !ymm.capabilities?.travelLinkSports &&
            !ymm.capabilities?.travelLinkStocks &&
            !ymm.capabilities?.trafficPlus &&
            !ymm.capabilities?.travelLinkWeather
        ) {
            setModalPage('noSupport');
            event.currentTarget.blur();
            return;
        }

        if (window && ymm.capabilities) {
            window.location.reload();
        }
    }

    const [isYmmShow, setIsYmmShow] = useState(false);
    useEffect(() => {
        const observer = new MutationObserver(() => {
            setIsYmmShow(document.documentElement.classList.contains('showYmm'));
        });

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class'],
        });
        return () => {
            observer.disconnect();
        };
    }, []);

    useEffect(() => {
        if (isYmmShow && ref.current) {
            ref.current.dispatchEvent(
                new CustomEvent('modalopen', { bubbles: true, detail: { pagename: MODAL_PAGE_NAME } }),
            );
        }
    }, [isYmmShow]);

    return (
        <>
            <FocusTrap
                active={isYmmShow}
                paused={focusPause}
                focusTrapOptions={{
                    allowOutsideClick: true,
                }}
            >
                <DisplayModal
                    ref={ref}
                    title="Vehicle Lookup"
                    onClose={props.onClose}
                    open={isYmmShow}
                    className={styles.modal}
                    pauseFocus
                    dataLocalpagename={MODAL_PAGE_NAME}
                >
                    <Text tag="h4" className={styles.lpHeading}>
                        {modalStrings.titles[modalPage]}
                    </Text>

                    {modalPage === 'noSupport' && (
                        <div className={styles.vehicleInfoWrapper}>
                            <Text tag="p">Your vehicle info:</Text>
                            <Text tag="p">{`${ymm.year} ${ymm.make} ${ymm.model}`}</Text>
                        </div>
                    )}

                    <Text tag="p">{modalStrings.infoTexts[modalPage]}</Text>

                    {modalPage === 'default' ? (
                        <div className={styles.ymmForm}>
                            <YMM.Form theme="standard" onCarReady={setYMMData} reset={isYmmShow} />
                            <InvalidFeedback className={styles.invalidFeedback} validState={ymmValid}>
                                Year, make and model required
                            </InvalidFeedback>
                        </div>
                    ) : null}

                    <Button
                        variant="primary"
                        type="button"
                        fitting="fill-container"
                        onClick={saveCookies}
                        className={styles.buttons}
                        disabled={!ymm.capabilities}
                        dataCy="molecules-ymm-infotainment-modal-show-packages-button"
                    >
                        {modalStrings.buttonTexts[modalPage]}
                    </Button>
                    {modalPage === 'onlyStreaming' && (
                        <Button
                            variant="secondary"
                            type="button"
                            fitting="fill-container"
                            className={styles.buttons}
                            onClick={() => {
                                setModalPage('default');
                            }}
                        >
                            Change vehicle info
                        </Button>
                    )}
                    {modalPage === 'default' && (
                        <Button
                            variant="tertiary"
                            className={styles.textButton}
                            type="button"
                            fitting="fill-container"
                            tertiaryNoPadding
                            onClick={() => {
                                setModalPage('onlyStreaming');
                            }}
                        >
                            Only have a streaming account?
                        </Button>
                    )}
                    {modalPage === 'noSupport' && (
                        <Button
                            variant="tertiary"
                            tertiaryNoPadding
                            type="button"
                            fitting="fill-container"
                            className={styles.noSupport}
                            onClick={() => {
                                setModalPage('default');
                            }}
                        >
                            Change vehicle info
                        </Button>
                    )}
                </DisplayModal>
            </FocusTrap>
        </>
    );
}
